import { Injectable, inject, signal } from '@angular/core';
import { EwarrantySettingService } from '@insite-store360/services';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map, of, tap } from 'rxjs';

export type EwLangCode = 'en' | 'vi';

export interface EwLanguageOption {
  code: EwLangCode;
  /** Shown in its own language, so it is intentionally NOT translated. */
  label: string;
}

/**
 * Owns language selection for the e-Warranty admin module only.
 *
 * English is the default; Vietnamese is the single alternate. The choice is a
 * PERSONAL preference saved on the signed-in user's account
 * (`/ewarrantySetting/my-language`), so one user switching to Vietnamese never
 * changes anyone else's screens. localStorage only caches it for the current
 * session so the first screen renders in the right language -- it cannot be
 * the source of truth, because logout runs localStorage.clear().
 *
 * Only e-Warranty templates use the translate pipe, so switching here does
 * not affect the rest of the app.
 */
@Injectable({ providedIn: 'root' })
export class EwLanguageService {
  private readonly translate = inject(TranslateService);
  private readonly settings = inject(EwarrantySettingService);

  private static readonly STORAGE_KEY = 'ew_lang';
  private static readonly DEFAULT: EwLangCode = 'en';

  readonly languages: readonly EwLanguageOption[] = [
    { code: 'en', label: 'English' },
    { code: 'vi', label: 'Tiếng Việt' },
  ];

  readonly current = signal<EwLangCode>(EwLanguageService.DEFAULT);

  /** The session the server preference was last loaded for. */
  private syncedToken: string | null = null;

  /**
   * Wire ngx-translate and apply the session's cached language. Called once at
   * boot. Returns the load observable so the app initializer waits for the
   * active language file before the first screen renders (keeps `instant()`
   * safe).
   */
  init(): Observable<unknown> {
    const codes = this.languages.map((l) => l.code);
    this.translate.addLangs(codes);
    this.translate.setDefaultLang(EwLanguageService.DEFAULT);
    return this.apply(this.readCached());
  }

  /**
   * Load the signed-in user's saved language, once per session. Run by the
   * e-Warranty route guard, so a user who signs in after the app booted still
   * gets their own language rather than the previous session's. Never blocks
   * navigation: a failed read leaves the current language in place.
   */
  syncForCurrentUser(): Observable<true> {
    const token = this.sessionToken();
    if (!token || token === this.syncedToken) return of(true);
    this.syncedToken = token;
    return this.settings.myLanguage().pipe(
      tap((saved) => {
        // No saved preference (never chosen, or not reachable): keep what this
        // session already shows. localStorage is cleared at logout, so that is
        // English for a fresh sign-in.
        if (this.isKnown(saved) && saved !== this.current()) this.apply(saved).subscribe();
      }),
      map(() => true as const)
    );
  }

  /** The user picked a language: apply it now and save it to their account. */
  setLanguage(code: EwLangCode): Observable<unknown> {
    const loaded$ = this.apply(code);
    this.settings.updateMyLanguage(code).subscribe({ error: () => undefined });
    return loaded$;
  }

  private apply(code: EwLangCode): Observable<unknown> {
    const loaded$ = this.translate.use(code);
    this.current.set(code);
    this.cache(code);
    return loaded$;
  }

  private isKnown(code: string | null | undefined): code is EwLangCode {
    return this.languages.some((l) => l.code === code);
  }

  private readCached(): EwLangCode {
    const saved = this.safeGet(EwLanguageService.STORAGE_KEY);
    return this.isKnown(saved) ? saved : EwLanguageService.DEFAULT;
  }

  private sessionToken(): string | null {
    return this.safeGet('auth-token');
  }

  private safeGet(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private cache(code: EwLangCode): void {
    try {
      localStorage.setItem(EwLanguageService.STORAGE_KEY, code);
    } catch {
      /* storage unavailable (e.g. SSR / privacy mode) — ignore */
    }
  }
}
